/**
 * Finish endpoint for the first-run wizard.
 *
 * `POST /api/system/setup/complete` stamps `setupCompletedAt` (ending the
 * bootstrap grace that keeps raw-IP access open), kicks the hardened
 * render+reload in the background, and returns:
 *   - consoleUrl: where the console lives from now on (null for IP-only
 *     evaluation clusters — no move, no hardening), and
 *   - handoffPath: a one-time `/api/auth/handoff?token=...` path that mints
 *     a session cookie on the NEW origin (cookies are host-scoped and can't
 *     follow the redirect from http://IP/ to https://<hostname>/).
 *
 * Raw fetch (not the Eden treaty client) — matches tls-api.ts's decoupling
 * rationale.
 */

export interface SetupCompleteResponse {
	consoleUrl: string | null;
	handoffPath: string | null;
}

export async function postSetupComplete(): Promise<SetupCompleteResponse> {
	const res = await fetch("/api/system/setup/complete", {
		method: "POST",
		credentials: "include",
	});

	let json: unknown = null;
	try {
		json = await res.json();
	} catch {
		// handled below
	}

	if (
		!res.ok ||
		!json ||
		typeof json !== "object" ||
		(json as { success?: unknown }).success !== true
	) {
		const v = json as { error?: { message?: string } } | null;
		throw new Error(v?.error?.message ?? `HTTP ${res.status}`);
	}

	const v = json as Record<string, unknown>;
	return {
		consoleUrl: typeof v.consoleUrl === "string" ? v.consoleUrl : null,
		handoffPath: typeof v.handoffPath === "string" ? v.handoffPath : null,
	};
}
